'use client';

/**
 * Login Form Component
 * Magento 2 production-style login form
 */
import { useEffect, useState } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { loginSchema } from '@/validators/customer.validator';
import { getLoginErrorMessage, INVALID_CREDENTIALS_MESSAGE } from '@/lib/login-error-message';
import { safeInternalPath } from '@/lib/safe-redirect';
export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = safeInternalPath(searchParams.get('callbackUrl'));
  const errorCode = searchParams.get('error');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(() => getLoginErrorMessage(errorCode));
  const [migrationMessage, setMigrationMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // NextAuth redirects its own failures to `pages.error` (this page) and can put
  // the literal string "undefined" in that parameter when the error carried no
  // type. The message is rendered from state above; the parameter itself is then
  // dropped from the address bar, so nothing ever shows a customer the word
  // "undefined", a reload does not resurrect a stale failure, and the URL they
  // might copy or bookmark is clean.
  useEffect(() => {
    if (errorCode === null) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    // `code` is Auth.js's companion parameter to `error`; leaving it behind
    // would keep an orphaned diagnostic in a customer-visible URL.
    url.searchParams.delete('code');
    router.replace(`${url.pathname}${url.search}`);
  }, [errorCode, router]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validation = loginSchema.safeParse({
      email,
      password
    });
    if (!validation.success) {
      setError(validation.error.errors[0]?.message || 'Validation failed');
      return;
    }
    setIsLoading(true);
    try {
      const result = await signIn('cognito-credentials', {
        email,
        password,
        redirect: false,
        callbackUrl
      });
      if (result?.error) {
        // Check if this user is migrated and needs a password reset
        const statusRes = await fetch('/api/auth/check-user-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email
          })
        });
        const statusData = await statusRes.json();
        if (statusData.requiresNewPassword) {
          // Show migration message — the reset link is in their inbox, so there
          // is nothing for them to type here.
          setMigrationMessage('Your account has been migrated to our new site. We\'ve emailed you a link to set a new password. Please check your inbox and click the link to continue.');
          return;
        }

        // Through the same map as `?error=`: `signIn()` returns Auth.js's own
        // enum names, and `setError(result.error)` used to print them verbatim,
        // so a misconfigured provider told the customer "Configuration". The
        // fallback is only reachable for an empty string, which `result.error`
        // being truthy already excludes.
        setError(getLoginErrorMessage(result.error) ?? INVALID_CREDENTIALS_MESSAGE);
      } else if (result?.ok) {
        const [callbackPathname = callbackUrl] = callbackUrl.split(/[?#]/, 1);
        if (callbackPathname === '/admin' || callbackPathname.startsWith('/admin/')) {
          window.location.assign(callbackUrl);
          return;
        }
        router.push(callbackUrl);
        router.refresh();
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <form onSubmit={handleSubmit}>
      {migrationMessage && <div className="account-alert account-alert--info mb-4">
          <p>{migrationMessage}</p>
          <p style={{
        marginTop: '8px'
      }}>
            <Link href="/forgot-password" className="login-forgot-link" style={{
          fontWeight: 600
        }}>
              Didn&apos;t get the email? Send it again →
            </Link>
          </p>
        </div>}

      {error && !migrationMessage && <div className="account-alert account-alert--warning mb-4">
          {error}
        </div>}

      <div className="login-form-group">
        <label htmlFor="login-email">
          Email <span className="required">*</span>
        </label>
        <input id="login-email" type="email" value={email} onChange={e_0 => setEmail(e_0.target.value)} required disabled={isLoading} autoComplete="email" />
      </div>

      <div className="login-form-group">
        <label htmlFor="login-password">
          Password <span className="required">*</span>
        </label>
        <input id="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e_1 => setPassword(e_1.target.value)} required disabled={isLoading} autoComplete="current-password" />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input id="show-password" type="checkbox" checked={showPassword} onChange={e_2 => setShowPassword(e_2.target.checked)} />
        <label htmlFor="show-password" className="text-sm text-gray-600 cursor-pointer select-none">
          Show Password
        </label>
      </div>

      <div className="login-actions">
        <button type="submit" className="login-btn" disabled={isLoading}>
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
        <Link href="/forgot-password" className="login-forgot-link">
          Forgot Your Password?
        </Link>
      </div>

      <p className="login-required-note">* Required Fields</p>
    </form>;
}